// SearchForm.js
import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Autocomplete,
  Grid,
  Typography,
  useTheme,
  Chip,
  FormControlLabel,
  Checkbox,
  Slider,
  Stack,
} from "@mui/material";

import { alpha } from "@mui/material/styles";
import { FlightTakeoff, FlightLand, Add, Remove, CompareArrows, ArrowForward } from "@mui/icons-material";
import { ToggleButtonGroup, ToggleButton } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PersonIcon from "@mui/icons-material/Person";
import HomeIcon from "@mui/icons-material/Home";
import StarIcon from "@mui/icons-material/Star";
import SearchIcon from "@mui/icons-material/Search";
import AssistantIcon from "@mui/icons-material/Assistant";
import CommentIcon from "@mui/icons-material/Comment";
import { Menu, MenuItem } from "@mui/material"; // Import Menu and MenuItem
import { lighten } from "@mui/system";
import { supabase } from "../../utils/supabaseClient";
import { AMENITIES_MAP } from "../../utils/amenitiesMap";
import DateRangeSelector from "./DateRangeSelector"; // ✅ Custom picker
import DateRangeExplore from "./DateRangeExplore"; // ✅ Custom picker for Explore tab
import AdvancedFiltersModal from "./Filters"; // adjust path if needed
import TuneIcon from "@mui/icons-material/Tune";
import Badge from "@mui/material/Badge";
import { logger } from "../../utils/logger";
import { useMediaQuery } from "@mui/material";
import { validateSearchFields } from "../../utils/searchValidation";
import {
  renderCityOption,
  cityAutocompleteSlotProps,
} from "../../utils/cityAutocompleteSlots";
import AddCardIcon from "@mui/icons-material/AddCard";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import ReviewsOutlinedIcon from "@mui/icons-material/ReviewsOutlined";
import AnalyticsOutlinedIcon from "@mui/icons-material/AnalyticsOutlined";

const SearchForm = ({
  originOptions,
  originInput,
  setOriginInput,
  origin,
  setOrigin,
  destinationOptions,
  destinationInput,
  setDestinationInput,
  destination,
  setDestination,
  departureDate,
  setDepartureDate,
  returnDate,
  setReturnDate,
  adults,
  setAdults,
  children,
  setChildren,
  filterAirports,
  filterCities,
  flightType,
  setFlightType,
  airbnbType,
  setAirbnbType,
  searchType,
  setSearchType,
  minReviewCount,
  setMinReviewCount,
  minReviewScore,
  setMinReviewScore,
  handleSearch,
  advancedFilters,
  setAdvancedFilters,
  setExploreResults,
  setSearchInitiated,
  setExploreLoading,
  setExploreFlightPrefs,
  transportMode,
  setTransportMode,
  travelTime,
  setTravelTime,
  onSearch,
  preferences,
  setPreferences,
  aiDescription,
  setAiDescription,
  tofly,
  setToFly,
  filtersOpen,
  setFiltersOpen,
  userCurrency,
  setUserCurrency,
  destinationVibe,
  setDestinationVibe,
}) => {
  const currencyRef = useRef("USD");
  useEffect(() => {
    if (userCurrency) {
      currencyRef.current = userCurrency;
      logger.log("🔄 currencyRef updated to:", userCurrency);
    }
  }, [userCurrency]);

  // This handles the initial load AND when switching back to flight mode.
  useEffect(() => {
    if (transportMode === "flight") {
      setToFly(true);
    } else {
      setToFly(false); // Optional: Uncheck if switching to drive/train
    }
  }, [transportMode, setToFly]);

  useEffect(() => {
    const fetchCurrency = async () => {
      const cached = localStorage.getItem("userCurrency");

      if (cached) {
        try {
          const parsed = JSON.parse(cached);
          const isToday =
            new Date(parsed.date).toDateString() === new Date().toDateString();

          if (isToday) {
            currencyRef.current = parsed.code;
            setUserCurrency(parsed.code);
            logger.log("💰 Using cached currency:", parsed.code);
            return;
          }
        } catch (err) {
          logger.warn("⚠️ Invalid cached currency. Refetching...");
        }
      }

      // Fallback: Fetch currency based on IP
      try {
        const response = await fetch("https://ipapi.co/json/");
        const data = await response.json();
        const currency = data.currency || "USD";

        currencyRef.current = currency;
        setUserCurrency(currency);

        localStorage.setItem(
          "userCurrency",
          JSON.stringify({
            code: currency,
            date: new Date().toISOString(),
          })
        );

        logger.log("🌍 IP-based currency set:", currency);
      } catch (err) {
        logger.error("🌍 Failed to fetch currency by IP:", err);
        currencyRef.current = "USD";
        setUserCurrency("USD");
      }
    };

    fetchCurrency();
  }, [setUserCurrency]);

  const getIPAddress = async () => {
    try {
      const response = await fetch("https://api64.ipify.org?format=json");
      const data = await response.json();
      return data.ip;
    } catch (error) {
      logger.error("Failed to fetch IP:", error);
      return "unknown"; // Return "unknown" if IP fetch fails
    }
  };

  // 🔹 NEW: Function to Log Search Data in Supabase
  const logSearchToSupabase = async () => {
    // Step 1: Get IP address
    const ipAddress = await getIPAddress();

    // Step 2: Get country, city, currency info from ipapi.co
    let country = "Unknown";
    let city = "Unknown";
    let currency = "Unknown";

    try {
      const response = await fetch(`https://ipapi.co/${ipAddress}/json/`);
      const data = await response.json();
      if (data) {
        country = data.country_name || "Unknown";
        city = data.city || "Unknown";
        currency = data.currency || "Unknown";
        // currencyRef.current = currency || "USD";
      }
    } catch (err) {
      logger.error("Failed to fetch IP metadata:", err);
    }

    // Step 3: Check if IP already exists in Supabase
    const { data, error } = await supabase
      .from("search_limits")
      .select("id, search_count")
      .eq("ip_address", ipAddress)
      .maybeSingle();

    if (error && error.code !== "PGRST116") {
      logger.error("Error checking search count:", error.message);
      return;
    }

    if (data) {
      // Step 4: If IP exists, update search_count + metadata
      await supabase
        .from("search_limits")
        .update({
          search_count: data.search_count + 1,
          country,
          city,
          currency,
        })
        .eq("id", data.id);

      logger.log("🔁 Updated search count to", data.search_count + 1);
      return;
    }

    // Step 5: If not found, insert new row
    await supabase.from("search_limits").insert([
      {
        ip_address: ipAddress,
        search_count: 1,
        country,
        city,
        currency,
      },
    ]);

    logger.log("🆕 New search entry created.");
  };

  const [errors, setErrors] = useState({});

  const [tripTypeAnchor, setTripTypeAnchor] = useState(null);
  const [adultsAnchor, setAdultsAnchor] = useState(null);

  const getSelectedFilterCount = () => {
    let count = 0;
    const keys = Object.keys(advancedFilters);
    for (const key of keys) {
      const value = advancedFilters[key];
      if (typeof value === "boolean" && value) count++;
      else if (Array.isArray(value) && value.length > 0) count++;
      else if (typeof value === "number" && value !== 0) count++;
    }
    return count;
  };

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const validateFields = () => {
    const newErrors = validateSearchFields({
      searchType,
      origin,
      destination,
      departureDate,
      returnDate,
      flightType,
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const normalizeString = (str) =>
    str
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase();

  const handleFormSubmit = () => {
    if (!validateFields()) return;

    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    if (!isMobile) {
      window.scrollTo({ top: 0, behavior: "auto" });
    }
    // ---------------------------------------------------------
    // 1️⃣ GOOGLE ANALYTICS TRACKING: SEARCH
    // ---------------------------------------------------------
    if (window.gtag) {
      window.gtag("event", "search_completed", {
        search_term: `${origin?.city || "Unknown"} to ${destination?.city || "Unknown"}`,
        origin_city: origin?.city,
        destination_city: destination?.city,
        travel_mode: transportMode,
        search_type: searchType,
        adults: adults,
        children: children,
      });
    }

    logSearchToSupabase(); // Still useful to keep!
    logger.log("💰 Detected Currency:", currencyRef.current);

    if (searchType === "explore") {
      onSearch({
        origin,
        departureDate,
        travelTime,
        transportMode,
        preferences,
        description: aiDescription,
        tofly,
        flightType,
        returnDate,
        adults,
        children,
        currency: userCurrency,
        destinationVibe: destinationVibe || "typical",
      });
    } else {
      handleSearch(
        searchType,
        advancedFilters,
        null,
        null,
        null,
        currencyRef.current
      );
    }
    // ✅ Scroll to top after search
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <>
      <Box
        id="search-form"
        sx={{
          width: "100%",
          maxWidth: "850px",
          mx: "auto",
          textAlign: "center",
          mb: { xs: 3, md: 5 },
          px: { xs: 2, sm: 3, md: 0 },
          pt: { xs: 1, md: 0 },
        }}
      >
        {/* A NICE COLORFUL TITLE */}
        <Typography
          component="h1"
          sx={{
            fontSize: "clamp(2rem, 6vw, 4rem)",
            fontWeight: 800,
            lineHeight: 1.2,
            mb: 2,
            background: theme.palette.mode === "light"
              ? "none"
              : "linear-gradient(45deg, #5F5AA2 30%, #FF5A5F 90%)",
            WebkitBackgroundClip: theme.palette.mode === "light" ? "unset" : "text",
            WebkitTextFillColor: theme.palette.mode === "light" ? theme.palette.primary.main : "transparent",
            // Mobile-only boosts
            [theme.breakpoints.down("sm")]: {
              fontSize: "2.75rem",
              lineHeight: 1.1,
              letterSpacing: "-0.03em",
            },
          }}
        >
          Your Entire Trip, Planned in One Search.
        </Typography>

        {/* A NICE SENTENCE OR TWO */}
        <Typography
          component="p"
          variant="h6"
          sx={{
            fontWeight: 400,
            color: theme.palette.mode === "light" ? "text.secondary" : "text.primary",
            lineHeight: 1.7,
            maxWidth: 900,
            mx: "auto",
            fontSize: { xs: "1.1rem", sm: "1.1rem", md: "1.25rem" },
            // Mobile-only boosts
            [theme.breakpoints.down("sm")]: {
              textShadow: theme.palette.mode === "light"
                ? "0 1px 8px rgba(0,0,0,0.18)"
                : "none",
              fontWeight: 500,
            },
          }}
        >
          Stop drowning in tabs trying to match flight dates to Airbnb availability.
          We combine both into a single, seamless search so you can book with confidence.
        </Typography>
      </Box>

      {/* Form Container */}
      <Box
        component="form"
        noValidate
        autoComplete="off"
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          width: { xs: "100%", md: "95%" }, // ✅ 100% on mobile, 90% on desktop
          maxWidth: "1200px",
          mx: "auto",
          mt: 0,
          mb: 1,

          gap: 1,
          background: theme.palette.mode === "dark" ? "transparent" : "#ffffff",
          border:
            theme.palette.mode === "dark"
              ? "1px solid #444"
              : "1px solid rgba(0,0,0,0.1)",
          borderRadius: { xs: 0, sm: "16px" },
          boxShadow:
            theme.palette.mode === "dark"
              ? "0px 6px 20px rgba(0, 0, 0, 0.5)"
              : "0px 20px 60px rgba(0, 0, 0, 0.25), 0px 4px 16px rgba(0, 0, 0, 0.12)",
          padding: { xs: "25px", md: "32px" },
        }}
      >
        {/* Search Type Selection */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            mt: 0,
            width: "100%",
          }}
        >
          <ToggleButtonGroup
            value={searchType}
            exclusive
            onChange={(e, newValue) => {
              if (newValue !== null) setSearchType(newValue);
            }}
            orientation={isMobile ? "vertical" : "horizontal"}
            sx={{
              width: {
                xs: "100%",
                md: "480px",
              },
              height: isMobile ? "auto" : "50px",
              mb: 1,
              borderRadius: "16px",
              overflow: "hidden",
              boxShadow: "0 1px 4px rgba(0, 0, 0, 0.1)",
              border: "1.2px solid gray",
              "& .MuiToggleButton-root": {
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: "500",
                fontSize: {
                  xs: "0.8rem",
                  md: "0.808rem",
                },
                color: "#858383",
                backgroundColor:
                  theme.palette.mode === "dark" ? "#1c1b1b" : "#fafafa",
                transition: "all 0.2s ease",
                border: "none",
                outline: "none",

                // Default Hover
                "&:hover": {
                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                },

                // Divider Lines logic (Vertical vs Horizontal)
                borderRight: !isMobile
                  ? `2px solid ${
                      theme.palette.mode === "light"
                        ? "rgba(0,0,0,0.5)"
                        : alpha(theme.palette.text.primary, 0.3)
                    }`
                  : "none",
                borderBottom: isMobile
                  ? `2px solid ${
                      theme.palette.mode === "light"
                        ? "rgba(0,0,0,0.5)"
                        : alpha(theme.palette.text.primary, 0.3)
                    }`
                  : "none",
                // On mobile, AI Explore is DOM-first but visually last — remove its bottom border
                "&:first-of-type": {
                  borderBottom: isMobile ? "none" : undefined,
                },
                "&:last-of-type": {
                  borderRight: "none",
                  // Don't remove borderBottom here — staysOnly is DOM-last but visually middle on mobile
                },
              },
            }}
          >
            {/* 1. AI EXPLORE — last on mobile, first on desktop */}
            <ToggleButton
              value="explore"
              aria-label="Explore"
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                order: { xs: 3, md: 1 },
                "&.Mui-selected": {
                  // Magical Purple Gradient
                  background:
                    "linear-gradient(135deg, #5F5AA2 0%, #8B7CF6 100%) !important",
                  color: "white !important",
                  "&:hover": {
                    background:
                      "linear-gradient(135deg, #4A4580 0%, #7B6BD4 100%) !important",
                  },
                },
              }}
            >
              <AutoAwesomeIcon
                sx={{
                  fontSize: 30,
                  // Color matches text when unselected
                  color: "inherit",
                }}
              />
              AI Explore
            </ToggleButton>

            {/* 2. FLIGHTS & AIRBNB — first on mobile */}
            <ToggleButton
              value="flightsAndStays"
              aria-label="Flights & Stays"
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                order: { xs: 1, md: 2 },
                "&.Mui-selected": {
                  backgroundColor: `${theme.palette.primary.flight} !important`,
                  color: "white !important",
                  "&:hover": {
                    backgroundColor: "#0588d4 !important",
                  },
                },
              }}
            >
              <img
                src={
                  searchType === "flightsAndStays"
                    ? theme.custom.searchTypeImages.flightsAndStays.selected
                    : theme.custom.searchTypeImages.flightsAndStays.unselected
                }
                alt="Flights Icon"
                style={{ width: 50, height: 40 }}
              />
              Flights & Airbnb
            </ToggleButton>

            {/* 3. AIRBNB ONLY — second on mobile */}
            <ToggleButton
              value="staysOnly"
              aria-label="Stays Only"
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                order: { xs: 2, md: 3 },
                "&.Mui-selected": {
                  backgroundColor: "#FF5A5F !important", // Airbnb Red
                  color: "white !important",
                  "&:hover": {
                    backgroundColor: "#E00B41 !important",
                  },
                },
              }}
            >
              <img
                src={
                  searchType === "staysOnly"
                    ? theme.custom.searchTypeImages.staysOnly.selected
                    : theme.custom.searchTypeImages.staysOnly.unselected
                }
                alt="Stays Icon"
                style={{ width: 50, height: 45 }}
              />
              Airbnb Only
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* Mode Context Label */}
        <Box sx={{ textAlign: "center", mt: 0.5, mb: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.875rem", letterSpacing: 0.2 }}>
            {searchType === "flightsAndStays" && "Search flights and Airbnbs together — see your combined trip cost instantly."}
            {searchType === "staysOnly" && "Find your perfect Airbnb — filter by rating, amenities, and more."}
            {searchType === "explore" && "Tell us what you're looking for and let AI suggest the perfect destination."}
          </Typography>
        </Box>

        {/* This Box is now mostly handled within the Explore section or below for other modes */}
        <Box
          sx={{
            display: searchType === "explore" ? "none" : "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
            maxWidth: "1200px",
            mx: "auto",
            mt: { xs: 0.5, md: 0 },
            mb: 1,
            gap: 0.5,
          }}
        >
          {/* Flight Type Dropdown Selector */}
          {searchType !== "staysOnly" &&
            searchType !== "explore" && ( // Ensure not explore
              <Button
                variant="outlined"
                onClick={(e) => setTripTypeAnchor(e.currentTarget)} // Open dropdown
                endIcon={<ExpandMoreIcon />} // Dropdown arrow
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  width: { xs: "auto", sm: "130px" },
                  textTransform: "none",
                  fontWeight: "bold",
                  color: theme.palette.mode === "dark" ? "white" : "black",
                  borderColor: theme.palette.mode === "dark" ? "#444" : "rgba(0,0,0,0.23)",
                  borderRadius: "8px",
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? lighten("#121212", 0.1)
                      : lighten("#121212", 0.99),
                  "&:hover": {
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? theme.palette.primary.main
                        : lighten(theme.palette.primary.main, 0.7),
                  },
                }}
              >
                {flightType === "roundtrip" ? "Round trip" : "One way"}
              </Button>
            )}

          {/* Adults Dropdown (Original position, hide if explore) */}
          <Button
            variant="outlined"
            onClick={(e) => setAdultsAnchor(e.currentTarget)}
            endIcon={<ExpandMoreIcon />}
            sx={{
              display: "flex",
              justifyContent: "space-between",
              width: { xs: "auto", sm: "130px" },
              textTransform: "none",
              fontWeight: "bold",
              color: theme.palette.mode === "dark" ? "white" : "black",
              borderColor: theme.palette.mode === "dark" ? "#444" : "rgba(0,0,0,0.23)",
              borderRadius: "8px",
              backgroundColor:
                theme.palette.mode === "dark"
                  ? lighten("#121212", 0.1)
                  : lighten("#121212", 0.99),
              "&:hover": {
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? theme.palette.primary.main
                    : lighten(theme.palette.primary.main, 0.7),
              },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <PersonIcon sx={{ fontSize: "1.2rem" }} />
              <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                {adults + children}
              </Typography>
            </Box>
          </Button>
        </Box>
        {/* Dropdown Menus */}
        <Menu
          anchorEl={tripTypeAnchor}
          open={Boolean(tripTypeAnchor)} // Open condition
          onClose={() => setTripTypeAnchor(null)} // Close menu
          MenuListProps={{ "aria-labelledby": "trip-type-selector" }}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
          sx={{
            "& .MuiPaper-root": {
              backgroundColor: theme.palette.background.paper, // ✅ Uses Light/Dark theme
              color: "theme.palette.text.primary", // ✅ Adapts text color
              minWidth: "150px", // Matches button width
              boxShadow: "none",
              border: `1px solid ${theme.palette.divider}`, // ✅ Adds a light border in both themes
            },
          }}
        >
          <MenuItem
            onClick={() => {
              setFlightType("roundtrip");
              setTripTypeAnchor(null);
            }}
            selected={flightType === "roundtrip"}
            sx={{
              "&.Mui-selected": {
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? theme.palette.primary.main //
                    : lighten(theme.palette.primary.main, 0.01), //
                color: "white",
              },
              "&:hover": {
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? lighten(theme.palette.primary.main, 0.3) //
                    : lighten(theme.palette.primary.main, 0.5), //
                color: theme.palette.mode === "dark" ? "white" : "black", // ✅ Set hover text color
              },
            }}
          >
            Round Trip
          </MenuItem>
          <MenuItem
            onClick={() => {
              setFlightType("oneway");
              setTripTypeAnchor(null);
            }}
            selected={flightType === "oneway"}
            sx={{
              "&.Mui-selected": {
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? theme.palette.primary.main //
                    : lighten(theme.palette.primary.main, 0.01), //
                color: "white",
              },
              "&:hover": {
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? lighten(theme.palette.primary.main, 0.3) //
                    : lighten(theme.palette.primary.main, 0.5), //
                color: theme.palette.mode === "dark" ? "white" : "black", // ✅ Set hover text color
              },
            }}
          >
            One Way
          </MenuItem>
        </Menu>
        {/* Adults Menu */}
        <Menu
          anchorEl={adultsAnchor}
          open={Boolean(adultsAnchor)}
          onClose={() => setAdultsAnchor(null)}
          sx={{
            "& .MuiPaper-root": {
              backgroundColor:
                theme.palette.mode === "dark" ? "#2c2c2c" : "#fafafa", // Or your desired light mode color
              boxShadow: "0px 2px 10px rgba(0, 0, 0, 0.3)",
              minWidth: "200px",
              padding: 0,
            },
          }}
        >
          {/* Adults */}
          <MenuItem
            disableRipple
            sx={{
              display: "flex",
              justifyContent: "space-between",
              "&:hover": {
                backgroundColor: "transparent", // Removes hover effect
              },
              "&.Mui-focusVisible": {
                backgroundColor: "transparent", // Removes focus effect
              },
            }}
          >
            <Typography variant="body1" sx={{ fontWeight: "bold" }}>
              Adults
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <IconButton
                onClick={() => setAdults(Math.max(1, adults - 1))}
                size="medium"
                sx={{
                  color: theme.palette.mode === "dark" ? "white" : "black",
                }}
              >
                <Remove />
              </IconButton>
              <Typography>{adults}</Typography>
              <IconButton
                onClick={() => setAdults(Math.min(10, adults + 1))}
                size="medium"
                sx={{
                  color: theme.palette.mode === "dark" ? "white" : "black",
                }}
              >
                <Add />
              </IconButton>
            </Box>
          </MenuItem>

          {/* Children */}
          <MenuItem
            disableRipple
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
              "&:hover": {
                backgroundColor: "transparent",
              },
              "&.Mui-focusVisible": {
                backgroundColor: "transparent",
              },
            }}
          >
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                Children
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: "text.secondary", mt: -0.5 }}
              >
                Ages 2-12
              </Typography>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <IconButton
                onClick={() => setChildren(Math.max(0, children - 1))}
                size="medium"
                sx={{
                  color: theme.palette.mode === "dark" ? "white" : "black",
                }}
              >
                <Remove />
              </IconButton>
              <Typography>{children}</Typography>
              <IconButton
                onClick={() => setChildren(Math.min(10, children + 1))}
                size="medium"
                sx={{
                  color: theme.palette.mode === "dark" ? "white" : "black",
                }}
              >
                <Add />
              </IconButton>
            </Box>
          </MenuItem>
        </Menu>
        {/* Origin and Destination Autocomplete Fields */}
        {searchType !== "explore" && (
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              gap: { xs: 2, md: 1 },
              width: { xs: "100%", md: "99%" },
              maxWidth: "1200px",
              justifyContent: "center",
              alignItems: "center",
              mx: "auto",
              mt: { xs: 1, md: 0.5 },
            }}
          >
            {/* Origin Autocomplete */}
            {searchType !== "staysOnly" && (
              <Autocomplete
                options={originOptions}
                renderOption={renderCityOption}
                slotProps={cityAutocompleteSlotProps(theme)}
                getOptionLabel={(option) => {
                  const parts =
                    option.country_code === "US" || option.country_code === "CA"
                      ? [option.city, option.admin, option.country_name]
                      : [option.city, option.country_name];
                  return parts.filter(Boolean).join(", ");
                }}
                inputValue={originInput}
                onInputChange={(e, newInputValue) => {
                  setOriginInput(newInputValue);
                  if (newInputValue === "") {
                    setOrigin(null);
                  }
                }}
                value={origin}
                onChange={(e, newValue) => {
                  setOrigin(newValue);
                  // ✅ FIX: Wait for the selection to finish before dismissing keyboard
                  setTimeout(() => {
                    if (document.activeElement instanceof HTMLElement) {
                      document.activeElement.blur();
                    }
                  }, 0);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Origin"
                    error={!!errors.origin}
                    helperText={errors.origin}
                    placeholder="City or airport code (e.g. LHR)"
                    variant="outlined"
                    fullWidth
                    inputProps={{ ...params.inputProps, style: { fontSize: "16px" } }}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment position="start">
                          <FlightTakeoff
                            sx={{
                              color:
                                theme.palette.mode === "dark"
                                  ? "white"
                                  : "#000",
                              fontSize: 20,
                            }}
                          />
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
                filterOptions={(options, { inputValue }) => {
                  const normalizedInput = normalizeString(inputValue.trim());
                  return options.filter((opt) => {
                    const label = [opt.city, opt.admin, opt.country_name]
                      .filter(Boolean)
                      .join(", ");
                    const normalizedLabel = normalizeString(label);
                    const iataMatch = opt.airports?.some(
                      (a) => normalizeString(a.iata || "").startsWith(normalizedInput)
                    );
                    return normalizedLabel.includes(normalizedInput) || iataMatch;
                  });
                }}
                sx={{
                  flex: 1,
                  minWidth: { xs: "99%", md: "50%" },
                  maxWidth: "300px",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor:
                      theme.palette.mode === "dark" ? "#666" : "rgba(0,0,0,0.23)",
                  },
                }}
              />
            )}

            {/* Destination Autocomplete */}
            <Autocomplete
              options={destinationOptions}
              renderOption={renderCityOption}
              slotProps={cityAutocompleteSlotProps(theme)}
              getOptionLabel={(option) => {
                const parts =
                  option.country_code === "US" || option.country_code === "CA"
                    ? [option.city, option.admin, option.country_name]
                    : [option.city, option.country_name];
                return parts.filter(Boolean).join(", ");
              }}
              inputValue={destinationInput}
              onInputChange={(e, newInputValue) => {
                setDestinationInput(newInputValue);
                if (newInputValue === "") {
                  setDestination(null);
                }
              }}
              value={destination}
              onChange={(e, newValue) => {
                setDestination(newValue);
                // ✅ FIX: Wait for the selection to finish before dismissing keyboard
                setTimeout(() => {
                  if (document.activeElement instanceof HTMLElement) {
                    document.activeElement.blur();
                  }
                }, 0);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={searchType === "staysOnly" ? "Where are you staying?" : "Destination"}
                  error={!!errors.destination}
                  helperText={errors.destination}
                  placeholder={searchType === "staysOnly" ? "Search any city or country" : "City or airport code (e.g. YYZ)"}
                  variant="outlined"
                  fullWidth
                  inputProps={{ ...params.inputProps, style: { fontSize: "16px" } }}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <InputAdornment position="start">
                        {searchType === "staysOnly" ? (
                          <HomeIcon sx={{ fontSize: 20 }} />
                        ) : (
                          <FlightLand
                            sx={{
                              color:
                                theme.palette.mode === "dark"
                                  ? "white"
                                  : "#000",
                              fontSize: 20,
                            }}
                          />
                        )}{" "}
                      </InputAdornment>
                    ),
                  }}
                />
              )}
              filterOptions={(options, { inputValue }) => {
                const normalizedInput = normalizeString(inputValue.trim());
                return options.filter((opt) => {
                  const label = [opt.city, opt.admin, opt.country_name]
                    .filter(Boolean)
                    .join(", ");
                  const normalizedLabel = normalizeString(label);
                  const iataMatch = opt.airports?.some(
                    (a) => normalizeString(a.iata || "").startsWith(normalizedInput)
                  );
                  return normalizedLabel.includes(normalizedInput) || iataMatch;
                });
              }}
              sx={{
                flex: 1,
                minWidth:
                  searchType === "staysOnly"
                    ? { xs: "100%", md: "100%" }
                    : { xs: "99%", md: "50%" },
                maxWidth: searchType === "staysOnly" ? "700px" : "400px",
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: theme.palette.mode === "dark" ? "#666" : "rgba(0,0,0,0.23)",
                },
              }}
            />
          </Box>
        )}
        {/* ✅ Not sure box? Explore!*/}
        {searchType !== "explore" && (
          <Box
            sx={{
              width: "99%",
              maxWidth: "1200px",
              display: "flex",
              justifyContent: "flex-end", // Aligns the link to the right
            }}
          >
            {/* ✅ Wrapper to align icon and text */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <AssistantIcon
                sx={{ fontSize: "1rem", color: "text.secondary" }}
              />
              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                  fontSize: "0.85rem", // Slightly larger for better readability
                  // Italic style is removed
                }}
              >
                Not sure where to go?{" "}
                <Box
                  component="span"
                  onClick={() => setSearchType("explore")}
                  sx={{
                    color: "#de5a6d",
                    fontWeight: 600, // Bolder for more emphasis
                    cursor: "pointer",
                    textDecoration: "underline",
                    "&:hover": {
                      textDecoration: "none",
                    },
                  }}
                >
                  Try AI Explore{" "}
                </Box>
              </Typography>
            </Box>
          </Box>
        )}
        {/* Date Range Selector */}
        {searchType !== "explore" && (
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              gap: 1.5,
              width: { xs: "100%", md: "100%" },
              maxWidth: "1200px",
              justifyContent: "center",
              alignItems: "center",
              mx: "auto",
              mt: { xs: 2, md: 0 }, // ✅ add top margin only on mobile
            }}
          >
            <DateRangeSelector
              departureDate={departureDate}
              setDepartureDate={setDepartureDate}
              returnDate={returnDate}
              setReturnDate={setReturnDate}
              errorStart={errors.departureDate}
              errorEnd={errors.returnDate}
              searchType={searchType}
              splitInputs
              flightType={flightType}
            />
          </Box>
        )}
        {/*
        {/* Stay Options BOX */}
        {searchType !== "explore" &&
          searchType !== "flightsAndStays" &&
          searchType !== "staysOnly" && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-evenly",
                width: "100%",
                maxWidth: "1200px",
                mx: "auto",
                mt: 0,
                mb: 0,
                flexWrap: "nowrap",
                flexDirection: { xs: "column", md: "row" },
              }}
            >
              {/* Airbnb Type Toggle Group with Icon */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",

                  flex: 1,
                  mb: { xs: 1, md: 0 },
                  width: { xs: "100%", md: "50%" },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    mb: 0.75,
                  }}
                >
                  <HomeIcon
                    sx={{
                      color: theme.palette.mode === "dark" ? "white" : "black",
                      fontSize: "17px",
                    }}
                  />
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: "normal",
                      color: "text.primary",
                      letterSpacing: "0.5px",
                    }}
                  >
                    Type of Airbnb
                  </Typography>
                </Box>

                <ToggleButtonGroup
                  value={airbnbType}
                  exclusive
                  onChange={(e, newValue) => {
                    if (newValue !== null) setAirbnbType(newValue);
                  }}
                  sx={{
                    width: { xs: "90%", md: "75%" },
                    justifyContent: "center", // ← center horizontally

                    borderRadius: "16px",
                    "& .MuiToggleButton-root": {
                      flex: 1,
                      textTransform: "none",
                      fontSize: "0.9rem",
                      padding: { xs: "12px 16px", md: "12px 16px" },
                      border: "2px solid",
                      borderColor: "divider",
                      whiteSpace: "nowrap",
                      "&.Mui-selected": {
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "primary"
                            : lighten(theme.palette.primary.main, 0.2),
                        color: "white",
                        "&:hover": {
                          backgroundColor: "primary",
                        },
                      },
                      "&:not(:first-of-type)": {
                        borderLeft: "none",
                      },
                      "&:first-of-type": {
                        borderTopLeftRadius: "8px",
                        borderBottomLeftRadius: "8px",
                      },
                      "&:last-of-type": {
                        borderTopRightRadius: "8px",
                        borderBottomRightRadius: "8px",
                      },
                    },
                  }}
                >
                  <ToggleButton value="both" aria-label="Any">
                    Any
                  </ToggleButton>
                  <ToggleButton value="entire_home" aria-label="Entire Home">
                    Entire Home
                  </ToggleButton>
                  <ToggleButton value="private_room" aria-label="Private Room">
                    Private Room
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>

              {/* New Review Score/Count Section */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  width: { xs: "100%", md: "50%" },
                  alignItems: "center",
                  justifyContent: "space-evenly",
                  gap: 0,
                  mx: "auto",
                  flexWrap: "nowrap",
                  mt: { xs: 1, md: 0 },
                  mb: 0,
                  textAlign: "center",
                }}
              >
                {/* Min. Rating */}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <StarIcon
                      sx={{
                        color:
                          theme.palette.mode === "dark" ? "white" : "black",
                        fontSize: "17px",
                      }}
                    />{" "}
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: "normal",
                        color: "text.primary",
                        letterSpacing: "0.5px",
                      }}
                    >
                      Min. Airbnb Rating
                    </Typography>
                  </Box>

                  <Box sx={{ display: "center", alignItems: "center", gap: 1 }}>
                    <TextField
                      value={minReviewScore}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === "" || /^[1-4](\.\d{0,2})?$/.test(value)) {
                          setMinReviewScore(value);
                        }
                      }}
                      onBlur={() => {
                        const numericValue = parseFloat(minReviewScore);
                        if (
                          isNaN(numericValue) ||
                          numericValue < 1 ||
                          numericValue >= 5
                        ) {
                          setMinReviewScore("4.00");
                        } else {
                          setMinReviewScore(numericValue.toFixed(2));
                        }
                      }}
                      type="text"
                      inputProps={{
                        inputMode: "decimal",
                        pattern: "^[1-4](\\.\\d{0,2})?$",
                        style: { textAlign: "center" },
                      }}
                      sx={{ width: "65px" }}
                    />
                  </Box>
                </Box>

                {/* Min. Reviews */}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <CommentIcon
                      sx={{
                        color:
                          theme.palette.mode === "dark" ? "white" : "black",
                        fontSize: "17px",
                      }}
                    />{" "}
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: "normal",
                        color: "text.primary",
                        letterSpacing: "0.5px",
                      }}
                    >
                      Min. Airbnb Reviews
                    </Typography>
                  </Box>
                  <Box sx={{ display: "center", alignItems: "center", gap: 1 }}>
                    <TextField
                      value={minReviewCount}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (
                          value === "" ||
                          (Number.isInteger(+value) &&
                            +value >= 0 &&
                            +value <= 5000)
                        ) {
                          setMinReviewCount(
                            value === "" ? "" : parseInt(value, 10)
                          );
                        }
                      }}
                      onBlur={() => {
                        if (minReviewCount === "" || minReviewCount < 0) {
                          setMinReviewCount(5);
                        } else if (minReviewCount > 5000) {
                          setMinReviewCount(5000);
                        }
                      }}
                      type="text"
                      inputProps={{
                        inputMode: "numeric",
                        pattern: "[0-9]*",
                        style: { textAlign: "center" },
                      }}
                      sx={{ width: "65px" }}
                    />
                  </Box>
                </Box>
              </Box>
            </Box>
          )}

        {/* ====== START EXPLORE MODE UI ====== */}
        {searchType === "explore" && (
          <Box sx={{ width: "100%", maxWidth: 1200, mx: "auto" }}>

            {/* Row 1: Origin | Travel Dates | Guests */}
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                gap: 1.5,
                mt: 0.5,
                mb: 1,
                alignItems: "stretch",
              }}
            >
              <Box sx={{ flex: 2, minWidth: 0 }}>
                <Autocomplete
                  options={originOptions}
                  renderOption={renderCityOption}
                  slotProps={cityAutocompleteSlotProps(theme)}
                  getOptionLabel={(option) => {
                    const parts =
                      option.country_code === "US" || option.country_code === "CA"
                        ? [option.city, option.admin, option.country_name]
                        : [option.city, option.country_name];
                    return parts.filter(Boolean).join(", ");
                  }}
                  inputValue={originInput}
                  onInputChange={(e, newInputValue) => {
                    setOriginInput(newInputValue);
                    if (newInputValue === "") setOrigin(null);
                  }}
                  value={origin}
                  onChange={(e, newValue) => setOrigin(newValue)}
                  filterOptions={(options, { inputValue }) => {
                    const normalizedInput = normalizeString(inputValue.trim());
                    return options.filter((opt) => {
                      const label = [opt.city, opt.admin, opt.country_name]
                        .filter(Boolean)
                        .join(", ");
                      const iataMatch = opt.airports?.some(
                        (a) => normalizeString(a.iata || "").startsWith(normalizedInput)
                      );
                      return normalizeString(label).includes(normalizedInput) || iataMatch;
                    });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Origin"
                      error={!!errors.origin}
                      helperText={errors.origin}
                      placeholder="City or airport code (e.g. LHR)"
                      variant="outlined"
                      fullWidth
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <InputAdornment position="start">
                            <FlightTakeoff />
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                  sx={{ "& .MuiOutlinedInput-notchedOutline": { borderColor: theme.palette.mode === "dark" ? "#666" : "rgba(0,0,0,0.23)" } }}
                />
              </Box>

              {/* Travel dates — narrower than origin */}
              <Box sx={{ flex: 1.4, minWidth: 0 }}>
                <DateRangeExplore
                  departureDate={departureDate}
                  returnDate={returnDate}
                  setDepartureDate={setDepartureDate}
                  setReturnDate={setReturnDate}
                  errorStart={errors.departureDate}
                  errorEnd={errors.returnDate}
                  searchType={searchType}
                />
              </Box>

              {/* Guests */}
              <Box sx={{ flex: "0 0 auto" }}>
                <Button
                  variant="outlined"
                  onClick={(e) => setAdultsAnchor(e.currentTarget)}
                  endIcon={<ExpandMoreIcon />}
                  sx={{
                    height: "56px",
                    px: 2,
                    textTransform: "none",
                    fontWeight: "bold",
                    color: theme.palette.mode === "dark" ? "white" : "black",
                    borderColor: theme.palette.mode === "dark" ? "#666" : "rgba(0,0,0,0.23)",
                    borderRadius: "4px",
                    whiteSpace: "nowrap",
                    backgroundColor: "transparent",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                    <PersonIcon sx={{ fontSize: "1.2rem" }} />
                    <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                      {adults + children}
                    </Typography>
                  </Box>
                </Button>
              </Box>
            </Box>

            {/* Row 2: Flight Type | Travel Mode (center) | Max Travel Time */}
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "center",
                gap: 3,
                mb: 2,
                flexWrap: "wrap",
              }}
            >
              {/* LEFT: Flight Type — always takes space, hidden when not flying */}
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.75, visibility: transportMode === "flight" ? "visible" : "hidden" }}>
                <Typography variant="caption" sx={{ fontSize: "0.75rem", color: "text.secondary", fontWeight: 500 }}>
                  Flight Type
                </Typography>
                <ToggleButtonGroup
                  value={flightType}
                  exclusive
                  size="small"
                  onChange={(e, newValue) => { if (newValue !== null) setFlightType(newValue); }}
                  sx={{
                    "& .MuiToggleButton-root.Mui-selected": {
                      background: "linear-gradient(135deg, #5F5AA2 0%, #8B7CF6 100%)",
                      color: "white",
                    },
                    "& .MuiToggleButton-root": { p: "6px 14px", fontSize: "0.82rem", backgroundColor: theme.palette.mode === "light" ? "#f1f3f4" : undefined },
                  }}
                >
                  <ToggleButton value="roundtrip">
                    <CompareArrows sx={{ fontSize: "0.95rem", mr: 0.5 }} />Roundtrip
                  </ToggleButton>
                  <ToggleButton value="oneway">
                    <ArrowForward sx={{ fontSize: "0.95rem", mr: 0.5 }} />One Way
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>

              {/* CENTER: Travel Mode */}
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.75 }}>
                <Typography variant="caption" sx={{ fontSize: "0.75rem", color: "text.secondary", fontWeight: 500 }}>
                  Travel Mode
                </Typography>
                <ToggleButtonGroup
                  value={transportMode}
                  exclusive
                  size="small"
                  onChange={(e, newValue) => {
                    if (newValue !== null) {
                      setTransportMode(newValue);
                      if (newValue === "flight") setToFly(true);
                    }
                  }}
                  sx={{
                    "& .MuiToggleButton-root.Mui-selected": {
                      background: "linear-gradient(135deg, #5F5AA2 0%, #8B7CF6 100%)",
                      color: "white",
                    },
                    "& .MuiToggleButton-root": { p: "6px 14px", fontSize: "0.82rem", backgroundColor: theme.palette.mode === "light" ? "#f1f3f4" : undefined },
                  }}
                >
                  <ToggleButton value="flight">✈️ Fly</ToggleButton>
                  <ToggleButton value="car">🚗 Drive</ToggleButton>
                  <ToggleButton value="train">🚄 Train</ToggleButton>
                </ToggleButtonGroup>
              </Box>

              {/* RIGHT: Max Travel Time */}
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.75, minWidth: 200, maxWidth: 280, flex: 1 }}>
                <Typography variant="caption" sx={{ fontSize: "0.75rem", color: "text.secondary", fontWeight: 500 }}>
                  ⏱ Max Travel Time
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, width: "100%" }}>
                  <Slider
                    value={travelTime}
                    onChange={(e, v) => setTravelTime(v)}
                    min={1} max={12} step={1}
                    size="small"
                    sx={{ flex: 1 }}
                  />
                  <Typography variant="caption" sx={{ fontSize: "0.82rem", whiteSpace: "nowrap", minWidth: 28 }}>
                    {travelTime}h
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Row 3+4: Trip Preferences — chips left, popularity right */}
            <Box sx={{ mb: 1.5 }}>
              <Typography variant="caption" sx={{ display: "block", textAlign: "center", fontSize: "0.72rem", color: "text.secondary", fontWeight: 600, mb: 1.25, textTransform: "uppercase", letterSpacing: "0.08em" }}>
                Trip Preferences
              </Typography>
              <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, alignItems: { xs: "center", sm: "flex-start" }, justifyContent: "center", gap: { xs: 1.5, sm: 3 } }}>

                {/* LEFT: Trip Style chips */}
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.75, width: { xs: "100%", sm: "auto" }, px: { xs: 2, sm: 0 } }}>
                  <Typography variant="caption" sx={{ fontSize: "0.72rem", color: "text.secondary", fontWeight: 500, letterSpacing: "0.04em" }}>
                    Trip Style
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, justifyContent: "center" }}>
                    {[
                      { label: "Any",       emoji: "✨" },
                      { label: "Beach",     emoji: "🏖️" },
                      { label: "City",      emoji: "🌆" },
                      { label: "Nature",    emoji: "🌿" },
                      { label: "Culture",   emoji: "🏛️" },
                      { label: "Romantic",  emoji: "🌹" },
                      { label: "Adventure", emoji: "🎒" },
                      { label: "Family",    emoji: "👨‍👩‍👧" },
                      { label: "Luxury",    emoji: "💎" },
                      { label: "Remote",    emoji: "🏔️" },
                    ].map(({ label, emoji }) => (
                      <Chip
                        key={label}
                        label={`${emoji} ${label}`}
                        clickable
                        size="small"
                        variant={preferences.includes(label) ? "filled" : "outlined"}
                        color={preferences.includes(label) ? "primary" : "default"}
                        onClick={() => {
                          if (label === "Any") {
                            setPreferences(["Any"]);
                          } else {
                            setPreferences((prev) =>
                              prev.includes(label)
                                ? prev.filter((p) => p !== label)
                                : [...prev.filter((p) => p !== "Any"), label]
                            );
                          }
                        }}
                        sx={{ height: "30px", fontSize: "0.82rem", "& .MuiChip-label": { px: 1 }, ...(theme.palette.mode === "light" && { backgroundColor: "#f1f3f4", borderColor: "#d0d0d0" }) }}
                      />
                    ))}
                  </Box>
                </Box>

                {/* Divider — vertical on desktop, horizontal on mobile */}
                <Box sx={{ width: { xs: "80%", sm: "1px" }, height: { xs: "1px", sm: "auto" }, alignSelf: { xs: "center", sm: "stretch" }, bgcolor: "divider", flexShrink: 0 }} />

                {/* RIGHT: Destination Vibe */}
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.75, flexShrink: 0 }}>
                  <Typography variant="caption" sx={{ fontSize: "0.72rem", color: "text.secondary", fontWeight: 500, letterSpacing: "0.04em" }}>
                    Destination Vibe
                  </Typography>
                  <ToggleButtonGroup
                    value={destinationVibe || "typical"}
                    exclusive
                    size="small"
                    onChange={(e, newValue) => {
                      if (newValue !== null && setDestinationVibe) setDestinationVibe(newValue);
                    }}
                    sx={{
                      "& .MuiToggleButton-root.Mui-selected": {
                        background: "linear-gradient(135deg, #5F5AA2 0%, #8B7CF6 100%)",
                        color: "white",
                      },
                      "& .MuiToggleButton-root": { p: "5px 12px", fontSize: "0.78rem", backgroundColor: theme.palette.mode === "light" ? "#f1f3f4" : undefined },
                    }}
                  >
                    <ToggleButton value="under_the_radar">🔭 Hidden</ToggleButton>
                    <ToggleButton value="typical">⚖️ Typical</ToggleButton>
                    <ToggleButton value="touristic">🗼 Famous</ToggleButton>
                  </ToggleButtonGroup>
                </Box>
              </Box>
            </Box>

            {/* Row 5: AI Description */}
            <Box sx={{ mb: 1 }}>
              <Typography variant="caption" sx={{ display: "block", fontSize: "0.72rem", color: "text.secondary", fontWeight: 500, mb: 0.5, letterSpacing: "0.04em" }}>
                Anything specific? (optional)
              </Typography>
              <TextField
                placeholder='e.g. "Somewhere warm with good food, not too touristy — we love hiking but also want a cozy Airbnb"'
                fullWidth
                multiline
                minRows={1}
                value={aiDescription}
                onChange={(e) => setAiDescription(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <AssistantIcon sx={{ fontSize: "1.2rem" }} />
                    </InputAdornment>
                  ),
                  sx: { fontSize: "1rem" },
                }}
              />
            </Box>
          </Box>
        )}
        {/* ====== END EXPLORE MODE UI ====== */}

        {/* ====== AIRBNB SMART FILTERS (staysOnly + flightsAndStays) ====== */}
        {(searchType === "staysOnly" || searchType === "flightsAndStays") && (
          <Box
            sx={{
              width: "100%",
              maxWidth: "1200px",
              mx: "auto",
              mt: 2,
              mb: 0,
              px: { xs: 1, md: 0 },
            }}
          >
            {/* Section title */}
            <Box sx={{ display: "flex", justifyContent: "center", mb: 1.5 }}>
              <Typography sx={{ fontSize: "0.75rem", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#FF8C00", whiteSpace: "nowrap" }}>
                ✦ Airbnb Filters
              </Typography>
            </Box>

            {/* Filter controls row */}
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                alignItems: { xs: "stretch", sm: "center" },
                justifyContent: "center",
                gap: { xs: 2, sm: 2 },
                flexWrap: "wrap",
              }}
            >
              {/* ⭐ Min Rating + 💬 Min Review — always horizontal */}
              <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, alignItems: "center", gap: { xs: 1.5, sm: 2 }, justifyContent: "center", width: { xs: "100%", sm: "auto" } }}>
                {/* ⭐ Min Rating Score */}
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5, minWidth: "90px" }}>
                  <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 500, display: "flex", alignItems: "center", gap: 0.5 }}>
                    <StarIcon sx={{ fontSize: "13px", color: "#FFB400" }} /> Min. Rating Score
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
                    <IconButton size="small" onClick={() => { const n = parseFloat(minReviewScore) || 4.0; setMinReviewScore(Math.max(1.0, Math.round((n - 0.1) * 10) / 10).toFixed(1)); }} sx={{ p: 0.25, color: "text.secondary" }}>
                      <Remove sx={{ fontSize: "16px" }} />
                    </IconButton>
                    <TextField
                      value={minReviewScore}
                      onChange={(e) => { const v = e.target.value; if (v === "" || /^[1-4](\.\d{0,2})?$/.test(v)) setMinReviewScore(v); }}
                      onFocus={(e) => e.target.select()}
                      onBlur={() => { const n = parseFloat(minReviewScore); setMinReviewScore(isNaN(n) || n < 1 || n >= 5 ? "4.0" : n.toFixed(1)); }}
                      type="text"
                      size="small"
                      inputProps={{ inputMode: "decimal", style: { textAlign: "center" } }}
                      sx={{ width: "60px" }}
                    />
                    <IconButton size="small" onClick={() => { const n = parseFloat(minReviewScore) || 4.0; setMinReviewScore(Math.min(4.9, Math.round((n + 0.1) * 10) / 10).toFixed(1)); }} sx={{ p: 0.25, color: "text.secondary" }}>
                      <Add sx={{ fontSize: "16px" }} />
                    </IconButton>
                  </Box>
                </Box>

                {/* 💬 Min Review Count */}
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5, minWidth: "100px" }}>
                  <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 500, display: "flex", alignItems: "center", gap: 0.5 }}>
                    <CommentIcon sx={{ fontSize: "13px", color: "#4A90E2" }} /> Min. Review Count
                  </Typography>
                  <TextField
                    value={minReviewCount}
                    onChange={(e) => { const v = e.target.value; if (v === "" || (Number.isInteger(+v) && +v >= 0 && +v <= 5000)) setMinReviewCount(v === "" ? "" : parseInt(v, 10)); }}
                    onBlur={() => { if (minReviewCount === "" || minReviewCount < 0) setMinReviewCount(5); else if (minReviewCount > 5000) setMinReviewCount(5000); }}
                    type="text"
                    size="small"
                    inputProps={{ inputMode: "numeric", style: { textAlign: "center" } }}
                    sx={{ width: "80px" }}
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        )}

        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            gap: 1,
            justifyContent: "center",
            alignItems: "center",
            mt: 3,
            mb: 2,
            width: "100%",
            px: { xs: 2, md: 0 },
          }}
        >
          {/* Filters Button with Badge */}
          <Badge
            sx={{ flex: "0 0 auto", display: "flex" }}
            badgeContent={getSelectedFilterCount()}
            color="primary"
            overlap="rectangular"
            invisible={getSelectedFilterCount() === 0}
          >
            <Button
              variant="outlined"
              color="secondary"
              onClick={() => setFiltersOpen(true)}
              startIcon={<TuneIcon />}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                width: { xs: "auto", md: "140px" },
                height: "50px",
                fontSize: { xs: "15px", md: "17px" },
                borderRadius: "12px",
                borderWidth: "2px",
                boxShadow: "none",
                whiteSpace: "nowrap",
                "&:hover": {
                  borderWidth: "2px",
                },
              }}
            >
              Filters
            </Button>
          </Badge>

          {/* Search Button */}
          <Button
            variant="contained"
            size="large"
            onClick={handleFormSubmit}
            startIcon={<SearchIcon />}
            sx={{
              flex: { xs: 1, md: "0 0 140px" },
              width: { xs: "auto", md: "140px" },
              height: "50px",
              fontSize: "17px",
              fontWeight: "bold",
              borderRadius: "12px",
              color: "white",
              border: "none",
              textTransform: "none",
              background: `linear-gradient(45deg, ${theme.palette.primary.stay} 0%, ${theme.palette.primary.main} 100%)`,
              transition: "transform 0.2s ease-in-out",
              "&:hover": {
                transform: "scale(1.05)",
              },
            }}
          >
            Search
          </Button>
        </Box>
        <AdvancedFiltersModal
          open={filtersOpen}
          onClose={() => setFiltersOpen(false)}
          searchType={searchType}
          advancedFilters={advancedFilters}
          setAdvancedFilters={setAdvancedFilters}
          AMENITIES_MAP={AMENITIES_MAP}
          airbnbType={airbnbType}
          setAirbnbType={setAirbnbType}
          minReviewScore={minReviewScore}
          setMinReviewScore={setMinReviewScore}
          minReviewCount={minReviewCount}
          setMinReviewCount={setMinReviewCount}
        />
      </Box>
    </>
  );
};

export default SearchForm;
